// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Platform, Share } from 'react-native';

export type ShareImageOutcome = 'shared' | 'downloaded' | 'cancelled' | 'unsupported';

type ExpoFile = {
  uri: string;
  exists: boolean;
  create: (options?: { overwrite?: boolean; intermediates?: boolean }) => void;
  write: (content: string, options?: { encoding?: string }) => void;
};

type ExpoFileSystemModule = {
  Paths: { cache: unknown };
  File: (new (...parts: unknown[]) => ExpoFile) & {
    /**
     * Streams a remote file straight to disk, headers included. Absent from builds made before
     * SDK 54, hence optional — see `downloadToCache`.
     */
    downloadFileAsync?: (
      url: string,
      to: unknown,
      options?: { headers?: Record<string, string>; idempotent?: boolean }
    ) => Promise<{ uri: string }>;
  };
};

type ExpoSharingModule = {
  isAvailableAsync: () => Promise<boolean>;
  shareAsync: (
    uri: string,
    options?: { mimeType?: string; UTI?: string; dialogTitle?: string }
  ) => Promise<void>;
};

/**
 * expo-file-system / expo-sharing are loaded lazily and defensively.
 *
 * Both are native modules, so they are absent from the web bundle and from any build made
 * before they were added to package.json. A missing module must degrade to a web download or
 * an "unsupported" result rather than taking the leaderboard screen down with it.
 */
const loadFileSystem = (): ExpoFileSystemModule | null => {
  try {
    // Metro only resolves require() with a literal, so both loaders repeat the call site.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const fs = require('expo-file-system') as ExpoFileSystemModule;
    return fs?.Paths?.cache && fs.File ? fs : null;
  } catch {
    return null;
  }
};

const loadSharing = (): ExpoSharingModule | null => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const sharing = require('expo-sharing') as ExpoSharingModule;
    return typeof sharing?.shareAsync === 'function' ? sharing : null;
  } catch {
    return null;
  }
};

const isCancellation = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  return message.includes('cancel') || message.includes('abort');
};

/**
 * iOS takes a Uniform Type Identifier rather than a MIME type, and gets the file wrong without
 * one — WhatsApp and Messages then offer the photo as a nondescript document. Anything unlisted
 * falls back to the generic image UTI, which still reaches the image-capable apps.
 */
const utiForMimeType = (mimeType: string): string => {
  switch (mimeType.toLowerCase()) {
    case 'image/png':
      return 'public.png';
    case 'image/jpeg':
    case 'image/jpg':
      return 'public.jpeg';
    case 'image/webp':
      return 'org.webmproject.webp';
    // Not an image: the user data export goes through the same download-and-share path.
    case 'application/json':
      return 'public.json';
    default:
      return 'public.image';
  }
};

/** Writes the PNG into the app cache and returns its file:// URI. */
const writeCacheFile = (base64: string, fileName: string): string => {
  const fs = loadFileSystem();
  if (!fs) {
    throw new Error('expo-file-system unavailable');
  }
  const file = new fs.File(fs.Paths.cache, fileName);
  file.create({ overwrite: true, intermediates: true });
  file.write(base64, { encoding: 'base64' });
  return file.uri;
};

/**
 * Downloads a remote image into the app cache and returns its file:// URI, or null when the
 * installed expo-file-system predates `downloadFileAsync`.
 *
 * Streaming to disk rather than going through `fetch` + base64 is deliberate: a 15 MB photo (the
 * upload cap) would otherwise become a ~20 MB base64 string held in JS memory on a phone.
 */
const downloadToCache = async (
  url: string,
  fileName: string,
  token: string | null | undefined
): Promise<string | null> => {
  const fs = loadFileSystem();
  if (!fs?.File?.downloadFileAsync) {
    return null;
  }
  const destination = new fs.File(fs.Paths.cache, fileName);
  const downloaded = await fs.File.downloadFileAsync(url, destination, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    // Sharing the same photo twice in a session hits an existing cache file; without this the
    // second share throws instead of overwriting.
    idempotent: true,
  });
  return downloaded.uri;
};

const shareBlobOnWeb = async (
  blob: Blob,
  fileName: string,
  mimeType: string,
  title: string
): Promise<ShareImageOutcome> => {
  const nav = navigator as Navigator & {
    canShare?: (data: { files?: File[] }) => boolean;
    share?: (data: { title?: string; text?: string; files?: File[] }) => Promise<void>;
  };

  if (typeof File === 'function' && typeof nav.share === 'function') {
    const file = new File([blob], fileName, { type: mimeType });
    // canShare is the only reliable way to know the browser accepts file payloads;
    // desktop Chrome exposes navigator.share but rejects files with a TypeError.
    if (nav.canShare?.({ files: [file] })) {
      try {
        await nav.share({ title, files: [file] });
        return 'shared';
      } catch (error) {
        if (isCancellation(error)) {
          return 'cancelled';
        }
        // fall through to the download path
      }
    }
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoking synchronously cancels the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'downloaded';
};

/** Hands a file:// URI in the app cache to the native share sheet. */
const shareCachedFileOnNative = async (
  uri: string,
  mimeType: string,
  title: string
): Promise<ShareImageOutcome> => {
  const sharing = loadSharing();

  if (sharing) {
    if (!(await sharing.isAvailableAsync())) {
      return 'unsupported';
    }
    try {
      await sharing.shareAsync(uri, { mimeType, UTI: utiForMimeType(mimeType), dialogTitle: title });
      return 'shared';
    } catch (error) {
      return isCancellation(error) ? 'cancelled' : 'unsupported';
    }
  }

  // Fallback for a build that predates expo-sharing. React Native's own Share sheet carries a
  // file URL on iOS only — on Android the url field is ignored, so there is nothing to send.
  if (Platform.OS !== 'ios') {
    return 'unsupported';
  }
  try {
    const result = await Share.share({ title, url: uri });
    return result.action === Share.dismissedAction ? 'cancelled' : 'shared';
  } catch (error) {
    return isCancellation(error) ? 'cancelled' : 'unsupported';
  }
};

/**
 * Hands a base64 PNG to the platform share sheet.
 *
 * `base64` is the raw payload without the `data:image/png;base64,` prefix, which is exactly what
 * react-native-svg's `toDataURL` returns on both web and native.
 */
export const sharePngBase64 = async (
  base64: string,
  fileName: string,
  title: string
): Promise<ShareImageOutcome> => {
  if (Platform.OS === 'web') {
    const blob = await (await fetch(`data:image/png;base64,${base64}`)).blob();
    return shareBlobOnWeb(blob, fileName, 'image/png', title);
  }
  return shareCachedFileOnNative(writeCacheFile(base64, fileName), 'image/png', title);
};

/**
 * Hands an image that lives behind a URL to the platform share sheet — WhatsApp, Messenger,
 * Instagram, Messages, or whatever else the device offers — as the actual file rather than a link.
 *
 * The URL may be authenticated (`token`), which is what makes this usable for a tour photo: the
 * byte route is member-gated, so the bytes have to be fetched with the viewer's bearer token
 * before the OS ever sees them. Nothing is made public by sharing this way; the recipient gets a
 * copy of the image in the app the user picked.
 *
 * Throws when the bytes cannot be fetched, so the caller can tell a failed download apart from a
 * device that has no share sheet.
 */
export const shareImageFromUrl = async ({
  url,
  token,
  fileName,
  mimeType,
  title,
}: {
  url: string;
  token?: string | null;
  fileName: string;
  mimeType: string;
  title: string;
}): Promise<ShareImageOutcome> => {
  if (Platform.OS === 'web') {
    const response = await fetch(url, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
    if (!response.ok) {
      throw new Error(`image request failed with ${response.status}`);
    }
    const blob = await response.blob();
    // The server is the authority on what it actually sent; `mimeType` is only the caller's guess
    // from the photo record, which predates the resize pipeline's rendition.
    return shareBlobOnWeb(blob, fileName, blob.type || mimeType, title);
  }

  const uri = await downloadToCache(url, fileName, token);
  return uri ? shareCachedFileOnNative(uri, mimeType, title) : 'unsupported';
};
